/**
 * Combobox — text input with a framework-styled dropdown of
 * filtered suggestions. Replaces the browser-native
 * `<input list="..."> + <datalist>` pattern whose dropdown is
 * rendered by the OS and ignores the app's design tokens.
 *
 * Free-text by design: typing a value not in `options` is allowed
 * (and emitted via `onChange`). The suggestions are a discoverability
 * affordance, not a constraint — matches the original audit-page
 * intent where an investigator may want to filter on an unknown
 * action key (e.g. one added by a deploy ahead of the frontend
 * mirror's update).
 *
 * Behavior:
 *   - Click the input → dropdown opens with all options.
 *   - Type → dropdown filters case-insensitively on substring match
 *     against each option.
 *   - Click an option → fills the input, closes the dropdown.
 *   - Click outside → closes without changing the value.
 *   - Escape → closes without changing the value.
 *   - Empty input → dropdown still opens (shows everything) so the
 *     user can browse the catalog.
 *
 * Accessibility (WAI-ARIA combobox pattern, PR #531 Copilot review):
 *   - Input carries `role="combobox"`, `aria-autocomplete="list"`,
 *     `aria-expanded` (live), and `aria-controls` (id of the listbox)
 *     so screen readers see the input as a combobox tied to the
 *     suggestion popup.
 *   - Listbox carries `role="listbox"`; each option is a `<li
 *     role="option">` with `aria-selected` reflecting the current
 *     value match.
 *   - The `<button>` inside each `<li>` keeps the click + focus
 *     affordance; the role lives on the `<li>` so assistive tech
 *     enumerates a list-of-options, not a list-of-buttons.
 *
 * Anti-pattern this avoids: a popover library / cmdk dependency for
 * a small bounded-purpose widget. The codebase's existing Header
 * app/variant dropdown uses the same hand-rolled click-outside +
 * ref pattern; this stays consistent with that.
 *
 * Known gap: no arrow-key navigation between options yet. Mouse +
 * click-to-select works; keyboard users land in the input via Tab
 * and can type to filter. Add arrow-key + Enter when a screen-
 * reader-using operator needs more.
 */
import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
} from 'react'
import { cn } from '@/lib/utils'
import { Input } from './input'

export interface ComboboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'list'> {
  value: string
  onChange: (value: string) => void
  /** Suggestion list. Filter is applied on the frontend per
   * keystroke; assume bounded sizes (~100s of items max). */
  options: readonly string[]
  /** Maximum number of suggestions to render. Defaults to 50; keep
   * the dropdown short enough to scan visually. */
  maxOptions?: number
}

/** Forward the input ref so form libraries (and focus-management
 * helpers) can drive the combobox like the other ui primitives
 * (`Input`, `Textarea`, `Select`). PR #531 Copilot review. */
export const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(function Combobox(
  { value, onChange, options, maxOptions = 50, className, id, ...inputProps },
  ref,
) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const listboxId = useId()

  // Close on outside click. Same pattern as the Header app/variant
  // dropdown — keeps the visual library narrow (no popover dep).
  useEffect(() => {
    if (!open) return
    function onMouseDown(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open])

  const trimmed = value.trim().toLowerCase()
  const filtered = trimmed
    ? options.filter((o) => o.toLowerCase().includes(trimmed))
    : options
  const visible = filtered.slice(0, maxOptions)
  const isExpanded = open && visible.length > 0

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      <Input
        {...inputProps}
        id={id}
        ref={ref}
        // ARIA combobox pattern — see jsdoc. `aria-expanded` MUST be
        // live (not just present); screen readers announce the
        // suggestion-list state change off this attribute.
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isExpanded}
        aria-controls={isExpanded ? listboxId : undefined}
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          if (!open) setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false)
            e.currentTarget.blur()
          }
        }}
        autoComplete="off"
      />
      {isExpanded && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover py-1 text-sm shadow-md"
        >
          {visible.map((opt) => {
            const selected = opt === value
            return (
              <li key={opt} role="option" aria-selected={selected}>
                <button
                  type="button"
                  tabIndex={-1}
                  // `onMouseDown` fires BEFORE the input's blur, so
                  // selecting an option doesn't race with the
                  // close-on-outside-click handler.
                  onMouseDown={(e) => {
                    e.preventDefault()
                    onChange(opt)
                    setOpen(false)
                  }}
                  className={cn(
                    'w-full text-left px-3 py-1.5 hover:bg-muted/60 focus:bg-muted/60 focus:outline-none font-mono text-xs',
                    selected && 'bg-muted/40 font-semibold',
                  )}
                >
                  {opt}
                </button>
              </li>
            )
          })}
          {filtered.length > maxOptions && (
            <li
              className="px-3 py-1.5 text-[10px] text-muted-foreground border-t mt-1"
              aria-hidden="true"
            >
              Showing first {maxOptions} of {filtered.length} matches — narrow your search to see more.
            </li>
          )}
        </ul>
      )}
    </div>
  )
})
